import React from 'react';

import { css } from '@emotion/core';
import isPropValid from '@emotion/is-prop-valid';
import styled from '@emotion/styled';
import { color } from '@styles/themes';
import { Button as AButton, ButtonProps } from 'antd';
import { tuple } from 'antd/lib/_util/type';
import { transparentize } from 'polished';

export const tYzTypeButton = tuple(
  'header-back',
  'footer-black-button',
  'footer-white-button',
  'footer-prev-button',
  'footer-next-button',
);

export type TYzTypeButton = (typeof tYzTypeButton)[number] | undefined;

type THandleButtonWithTypeParams = {
  yzTypeButton?: TYzTypeButton;
};

interface IStyledbutton extends ButtonProps {
  /** Button Style */
  yzTypeButton?: TYzTypeButton;
  isInputButton?: boolean;
  round?: boolean;
}

export interface IButtonParams extends IStyledbutton {
  buttonText?: string | React.ReactNode;
}

const handleButtonWithType = ({ yzTypeButton }: THandleButtonWithTypeParams) => {
  switch (yzTypeButton) {
    case 'footer-next-button':
      return css`
        flex: 1 1 0;
        height: 52px;
        border: 1px solid #2b8fff;
        border-radius: 20px;
        background: #2b8fff;

        :active,
        :focus,
        :hover {
          border: 1px solid #2b8fff;
          background: #2b8fff;
        }

        > span {
          color: #ffffff;
          font-size: 16px;
          line-height: 1em;
          font-weight: 700;
        }
        &[disabled],
        &[disabled]:hover {
          border: none;
          background: ${transparentize(0.3, '#c4c4c4')};
        }
      `;
    case 'footer-prev-button':
      return css`
        flex: 1 1 0;
        height: 52px;
        border: 1px solid #afafaf;
        border-radius: 20px;
        background: #ffffff;

        > span {
          color: #1d1c1d;
          font-size: 16px;
          line-height: 1em;
          font-weight: 700;
        }
      `;
    case 'header-back':
      return css`
        position: absolute;
        width: 10px;
        height: 30px;
        line-height: 20px;
        left: 0px;
        top: 17px;
        border: none;
        background: ${color.main1};
        color: ${color.white};

        :hover {
          cursor: pointer;
        }

        :active,
        :focus,
        :hover {
          background: ${color.main1};
          color: ${color.white};
        }
      `;
    case 'footer-white-button':
      return css`
        width: 100%;
        height: 52px;
        border: none;
        background: ${color.white};
        color: ${color.infoText};
        border: 1px solid #afafaf;

        font-size: 16px;
        line-height: 19px;
        letter-spacing: 0.005em;
        font-weight: 700;

        :hover {
          cursor: pointer;
        }

        :active,
        :focus,
        :hover {
          background: ${color.white};
          color: ${color.infoText};
        }

        &[disabled],
        &[disabled]:hover {
          border: none;
          background: ${transparentize(0.3, color.disabled)};
        }
      `;
    case 'footer-black-button':
      return css`
        width: 100%;
        height: 52px;
        border: none;
        background: ${color.footerButton};
        color: ${color.white} !important;

        font-size: 16px;
        line-height: 19px;
        letter-spacing: 0.005em;
        font-weight: 700;

        :hover {
          cursor: pointer;
        }

        :active,
        :focus,
        :hover {
          background: ${color.footerButton};
          color: ${color.white};
        }

        &[disabled],
        &[disabled]:hover {
          border: none;
          background: ${transparentize(0.3, color.disabled)};
        }
      `;
    default:
      return css``;
  }
};

export const StyledButton = styled(AButton, { shouldForwardProp: isPropValid })<IStyledbutton>`
  height: 36px;
  border-radius: 2px;
  font-size: 18px;
  font-family: Lato, NotoSansCJKkr, sans-serif;

  & svg,
  & img {
    margin-right: 4px;
  }

  & span {
    margin-top: 2px;
  }

  ${({ yzTypeButton }) => handleButtonWithType({ yzTypeButton })}
`;

export const Button = ({ buttonText, children, ...rest }: IButtonParams) => {
  switch (rest.yzTypeButton) {
    default:
      return <StyledButton {...rest}>{buttonText || children}</StyledButton>;
  }
};
